import React, { Component } from "react";
import PropTypes from "prop-types";
// import { mean } from "d3-array";

import { path as d3Path } from "d3-path";

import { last, noop } from "./utils";
import { isLastBarVisibleInExtents } from "../../components/ChartWrapper/utils/chartHorizontalState.js";

class ZoomButtons extends Component {
  constructor(props) {
    super(props);
    this.handleZoomOut = this.handleZoomOut.bind(this);
    this.handleZoomIn = this.handleZoomIn.bind(this);
    this.handleScrollToEnd = this.handleScrollToEnd.bind(this);
    this.zoom = this.zoom.bind(this);
    this.isLastBarVisible = this.isLastBarVisible.bind(this);

    // Cache for shouldComponentUpdate optimization
    this._lastWidth = null;
    this._lastHeight = null;
    this._lastShowScrollButton = null;
  }

  // POC: Skip re-render if nothing visual changed
  shouldComponentUpdate(nextProps, nextState) {
    const { chartConfig } = this.context;
    const { width, height } = chartConfig;

    // Check if scroll button visibility changed
    const showScrollButton = !this.isLastBarVisible();

    // Only re-render if something visual changed
    const shouldUpdate =
      width !== this._lastWidth ||
      height !== this._lastHeight ||
      showScrollButton !== this._lastShowScrollButton ||
      nextProps.showZoomControls !== this.props.showZoomControls ||
      nextProps.onReset !== this.props.onReset ||
      nextProps.textFill !== this.props.textFill ||
      nextProps.fill !== this.props.fill ||
      nextProps.stroke !== this.props.stroke;

    // Cache current values
    this._lastWidth = width;
    this._lastHeight = height;
    this._lastShowScrollButton = showScrollButton;

    return shouldUpdate;
  }

  isLastBarVisible() {
    const { xScale, plotData, fullData, xAccessor } = this.context;
    if (!plotData || plotData.length === 0) return true;

    // Use fullData to get the actual last bar of all data (not just visible)
    const allData = fullData || plotData;
    const lastItemOfAll = last(allData);
    if (!lastItemOfAll) return true;

    const [start, end] = xScale.domain();
    const lastItemX = xAccessor(lastItemOfAll);
    return isLastBarVisibleInExtents([start, end], lastItemX);
  }

  zoom(direction) {
    const { xAxisZoom, xScale, plotData, xAccessor } = this.context;
    const cx = xScale(xAccessor(last(plotData)));
    // mean(xScale.range());
    const { zoomMultiplier, minBarsVisible } = this.props;

    const c = direction > 0 ? 1 * zoomMultiplier : 1 / zoomMultiplier;

    const [newStart, newEnd] = xScale
      .range()
      .map((x) => cx + (x - cx) * c)
      .map(xScale.invert);

    // Limit zoom in: ensure minimum bars are visible
    const barsInView = newEnd - newStart;
    if (direction < 0 && barsInView < minBarsVisible) {
      // Don't allow further zoom in
      return;
    }

    // Apply zoom instantly without animation
    xAxisZoom([newStart, newEnd]);
  }

  handleScrollToEnd() {
    const { xAxisZoom, xScale, plotData, fullData, xAccessor } = this.context;
    if (!plotData || plotData.length === 0) return;

    const [currentStart, currentEnd] = xScale.domain();
    const domainWidth = currentEnd - currentStart;

    // Use fullData to get the actual last bar
    const allData = fullData || plotData;
    const lastItem = last(allData);
    const lastItemX = xAccessor(lastItem);

    // Add a small buffer (0.5 bar) to ensure last bar is fully visible
    const buffer = 0.5;

    // New domain: maintain same width, but end slightly past last item
    const newEnd = lastItemX + buffer;
    const newStart = newEnd - domainWidth;

    // Apply scroll instantly without animation
    xAxisZoom([newStart, newEnd]);
  }

  handleZoomOut() {
    this.zoom(1);
  }
  handleZoomIn() {
    this.zoom(-1);
  }
  render() {
    const { chartConfig } = this.context;
    const { width, height } = chartConfig;
    const { size, heightFromBase, rx, ry } = this.props;
    const { stroke, strokeOpacity, fill, strokeWidth, fillOpacity } =
      this.props;
    const { textFill, textStrokeWidth } = this.props;
    const { onReset, showZoomControls } = this.props;

    // Button should be ENABLED when last bar is NOT visible
    const lastBarVisible = this.isLastBarVisible();
    const showScrollButton = !lastBarVisible;

    const centerX = Math.round(width / 2);
    const y = height - heightFromBase;

    const [w, h] = size;
    const hLength = 5;
    const wLength = 6;

    const textY = Math.round(y + h / 2);

    const resetX = centerX;

    const zoomOut = d3Path();
    const zoomOutX = centerX - w - 2 * strokeWidth;
    zoomOut.moveTo(zoomOutX - wLength, textY);
    zoomOut.lineTo(zoomOutX + wLength, textY);
    zoomOut.closePath();

    const zoomIn = d3Path();
    const zoomInX = centerX + w + 2 * strokeWidth;

    zoomIn.moveTo(zoomInX - wLength, textY);
    zoomIn.lineTo(zoomInX + wLength, textY);

    zoomIn.moveTo(zoomInX, textY - hLength);
    zoomIn.lineTo(zoomInX, textY + hLength);
    // zoomIn.closePath();

    // Scroll to end button (right arrow with end line)
    const scrollToEnd = d3Path();
    const scrollX = zoomInX + w + 2 * strokeWidth;

    // Draw arrow pointing right
    scrollToEnd.moveTo(scrollX - 4, textY - 4);
    scrollToEnd.lineTo(scrollX + 1, textY);
    scrollToEnd.lineTo(scrollX - 4, textY + 4);

    // Draw vertical line to indicate "end"
    scrollToEnd.moveTo(scrollX + 3, textY - 5);
    scrollToEnd.lineTo(scrollX + 3, textY + 5);

    return (
      <g className="react-stockcharts-zoom-button">
        {showZoomControls && (
          <g>
            <rect
              x={zoomOutX - w / 2}
              y={y}
              rx={rx}
              ry={ry}
              height={h}
              width={w}
              fill={fill}
              fillOpacity={fillOpacity}
              stroke={stroke}
              strokeOpacity={strokeOpacity}
              strokeWidth={strokeWidth}
            />
            <path
              d={zoomOut.toString()}
              stroke={textFill}
              strokeWidth={textStrokeWidth}
            />
            <rect
              x={resetX - w / 2}
              y={y}
              rx={rx}
              ry={ry}
              height={h}
              width={w}
              fill={fill}
              fillOpacity={fillOpacity}
              stroke={stroke}
              strokeOpacity={strokeOpacity}
              strokeWidth={strokeWidth}
            />
            <g transform={`translate (${resetX}, ${y + h / 4}) scale(.14)`}>
              <path
                d="M31 13C23.4 5.3 12.8.5 1.1.5c-23.3 0-42.3 19-42.3 42.5s18.9 42.5 42.3 42.5c13.8 0 26-6.6 33.7-16.9l-16.5-1.8C13.5 70.4 7.5 72.5 1 72.5c-16.2 0-29.3-13.2-29.3-29.4S-15.2 13.7 1 13.7c8.1 0 15.4 3.3 20.7 8.6l-10.9 11h32.5V.5L31 13z"
                fill={textFill}
              />
            </g>
            <rect
              x={zoomInX - w / 2}
              y={y}
              rx={rx}
              ry={ry}
              height={h}
              width={w}
              fill={fill}
              fillOpacity={fillOpacity}
              stroke={stroke}
              strokeOpacity={strokeOpacity}
              strokeWidth={strokeWidth}
            />
            <path
              d={zoomIn.toString()}
              stroke={textFill}
              strokeWidth={textStrokeWidth}
            />
          </g>
        )}

        {/* Scroll to end button - only visible when last bar is not visible */}
        {/* Subtle pulse to hint the chart is displaced */}
        {showScrollButton && (
          <g>
            <rect
              x={scrollX - w / 2}
              y={y}
              rx={rx}
              ry={ry}
              height={h}
              width={w}
              fill="#ffffff"
              stroke={stroke}
              strokeOpacity={strokeOpacity}
              strokeWidth={strokeWidth}
            >
              <animate
                attributeName="fill-opacity"
                values="0.15;0.55;0.15"
                dur="1.6s"
                repeatCount="indefinite"
              />
            </rect>
            <path
              d={scrollToEnd.toString()}
              stroke={textFill}
              strokeWidth={textStrokeWidth}
              fill="none"
            />
            <rect
              className="react-stockcharts-enable-interaction scroll-to-end"
              onClick={this.handleScrollToEnd}
              x={scrollX - w / 2}
              y={y}
              rx={rx}
              ry={ry}
              height={h}
              width={w}
              fill="none"
              data-evo-id="zoombuttons-rect-zoom-out"
            />
          </g>
        )}

        {showZoomControls && (
          <g>
            <rect
              className="react-stockcharts-enable-interaction out"
              onClick={this.handleZoomOut}
              x={zoomOutX - w / 2}
              y={y}
              rx={rx}
              ry={ry}
              height={h}
              width={w}
              fill="none"
              data-evo-id="zoombuttons-rect-reset"
            />
            <rect
              className="react-stockcharts-enable-interaction reset"
              onClick={onReset}
              x={resetX - w / 2}
              y={y}
              rx={rx}
              ry={ry}
              height={h}
              width={w}
              fill="none"
              data-evo-id="zoombuttons-rect-pan"
            />
            <rect
              className="react-stockcharts-enable-interaction in"
              onClick={this.handleZoomIn}
              x={zoomInX - w / 2}
              y={y}
              rx={rx}
              ry={ry}
              height={h}
              width={w}
              fill="none"
              data-evo-id="zoombuttons-rect-pan-in"
            />
          </g>
        )}
      </g>
    );
  }
}

ZoomButtons.propTypes = {
  zoomMultiplier: PropTypes.number.isRequired,
  minBarsVisible: PropTypes.number,
  size: PropTypes.array.isRequired,
  heightFromBase: PropTypes.number.isRequired,
  rx: PropTypes.number.isRequired,
  ry: PropTypes.number.isRequired,
  stroke: PropTypes.string.isRequired,
  strokeWidth: PropTypes.number.isRequired,
  strokeOpacity: PropTypes.number.isRequired,
  fill: PropTypes.string.isRequired,
  fillOpacity: PropTypes.number.isRequired,
  fontSize: PropTypes.number.isRequired,
  textDy: PropTypes.string.isRequired,
  textFill: PropTypes.string.isRequired,
  textStrokeWidth: PropTypes.number.isRequired,
  onReset: PropTypes.func,
  showZoomControls: PropTypes.bool,
};

ZoomButtons.defaultProps = {
  size: [30, 24],
  heightFromBase: 80,
  rx: 3,
  ry: 3,
  stroke: "#000000",
  strokeOpacity: 0.3,
  strokeWidth: 1,
  fill: "#D6D6D6",
  fillOpacity: 0.4,
  fontSize: 16,
  textDy: ".3em",
  textFill: "#000000",
  textStrokeWidth: 2,
  zoomMultiplier: 1.5,
  minBarsVisible: 6,
  onReset: noop,
  showZoomControls: false,
};

ZoomButtons.contextTypes = {
  xScale: PropTypes.func.isRequired,
  chartConfig: PropTypes.object.isRequired,
  plotData: PropTypes.array.isRequired,
  fullData: PropTypes.array,
  xAccessor: PropTypes.func.isRequired,
  xAxisZoom: PropTypes.func.isRequired,
};

export default ZoomButtons;
